import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { AdminShell } from './layouts/AdminShell';
import { MobileShell } from './layouts/MobileShell';
import { RequireAdmin } from './lib/auth/require-admin';
import { StepUpProvider } from './lib/auth/step-up';
import { AssistantProvider } from './lib/assistant';
import { useIsMobile } from './lib/use-is-mobile';

function RootShell() {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileShell>
      <Outlet />
    </MobileShell>
  ) : (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

/**
 * RequireAdmin 을 통과한 이후에만 Step-up / Assistant 컨텍스트가 마운트된다.
 *
 * 순서 (바깥 → 안쪽):
 *   RequireAdmin → AssistantProvider(AI 세팅·예산) → StepUpProvider(2FA) → RootShell(Shell)
 *
 * Assistant 은 Dashboard(#6) 등 보호된 페이지에서만 필요하므로 RequireAdmin 안쪽.
 * StepUpProvider 는 Assistant 과 독립적이지만 동일 레벨에서 공존.
 */
function ProtectedShell() {
  return (
    <RequireAdmin>
      <AssistantProvider>
        <StepUpProvider>
          <RootShell />
        </StepUpProvider>
      </AssistantProvider>
    </RequireAdmin>
  );
}

/**
 * Admin 라우트 (docs/specs/superadmin.md §2.1).
 * 4섹션 · 10페이지 + `/` → `/overview/dashboard` redirect.
 *
 * `/403` 은 RequireAdmin 밖(public). 그 외 모든 라우트는 ProtectedShell 하위.
 */
export const router = createBrowserRouter([
  {
    path: '/403',
    lazy: async () => ({ Component: (await import('./pages/_errors/Forbidden')).default }),
  },
  {
    path: '/',
    element: <ProtectedShell />,
    children: [
      { index: true, element: <Navigate to="/overview/dashboard" replace /> },

      // ─── Overview ───
      {
        path: 'overview/dashboard',
        lazy: async () => ({ Component: (await import('./pages/overview/Dashboard')).default }),
      },

      // ─── People ───
      {
        path: 'people/members',
        lazy: async () => ({ Component: (await import('./pages/people/Members')).default }),
      },
      {
        path: 'people/credits',
        lazy: async () => ({ Component: (await import('./pages/people/Credits')).default }),
      },
      {
        path: 'people/feedback',
        lazy: async () => ({ Component: (await import('./pages/people/Feedback')).default }),
      },

      // ─── AI Studio ───
      {
        path: 'studio/lab',
        lazy: async () => ({ Component: (await import('./pages/studio/Lab')).default }),
      },
      {
        path: 'studio/treatments',
        lazy: async () => ({ Component: (await import('./pages/studio/Treatments')).default }),
      },
      {
        path: 'studio/experiments',
        lazy: async () => ({ Component: (await import('./pages/studio/Experiments')).default }),
      },
      {
        path: 'studio/golden-set',
        lazy: async () => ({ Component: (await import('./pages/studio/GoldenSet')).default }),
      },
      {
        path: 'studio/incidents',
        lazy: async () => ({ Component: (await import('./pages/studio/Incidents')).default }),
      },

      // ─── Settings ───
      {
        path: 'settings',
        lazy: async () => ({ Component: (await import('./pages/Settings')).default }),
      },

      // ─── Fallback ───
      { path: '*', element: <Navigate to="/overview/dashboard" replace /> },
    ],
  },
]);
